import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { KanbanBoard } from '../components/KanbanBoard';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { Plus, X, Edit, Trash2, Calendar, CheckCircle2 } from 'lucide-react';

const TASK_COLUMNS = [
  { id: 'todo', title: 'To Do', color: 'gray' },
  { id: 'in_progress', title: 'In Progress', color: 'blue' },
  { id: 'review', title: 'Review', color: 'yellow' },
  { id: 'done', title: 'Done', color: 'green' },
];

const PRIORITY_COLORS = {
  low: 'bg-gray-100 text-gray-600 border-gray-200',
  medium: 'bg-yellow-100 text-yellow-700 border-yellow-200',
  high: 'bg-red-100 text-red-700 border-red-200',
};

const TaskCard = ({ task, onEdit, onDelete, users, projects }) => {
  const assignedUser = users.find(u => u.id === task.assigned_to);
  const project = projects.find(p => p.id === task.project_id);
  
  return (
    <div className={`bg-white rounded-xl p-4 border-l-4 shadow-sm hover:shadow-md transition-shadow group ${
      task.priority === 'high' ? 'border-l-red-500' :
      task.priority === 'medium' ? 'border-l-yellow-500' :
      'border-l-gray-300'
    }`}>
      <div className="flex items-start justify-between mb-2">
        <h4 className="font-medium text-[#0A1628] text-sm">{task.title}</h4>
        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <button onClick={() => onEdit(task)} className="p-1 text-gray-400 hover:text-[#014DB7]">
            <Edit className="w-3.5 h-3.5" />
          </button>
          <button onClick={() => onDelete(task)} className="p-1 text-gray-400 hover:text-red-500">
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
      
      {task.description && (
        <p className="text-xs text-gray-500 mb-3 line-clamp-2">{task.description}</p>
      )}

      <div className="flex items-center justify-between text-xs">
        <div className="flex items-center gap-2">
          {project && (
            <span className="bg-[#014DB7]/10 text-[#014DB7] px-2 py-0.5 rounded">
              {project.name}
            </span>
          )}
          <span className={`px-2 py-0.5 rounded ${PRIORITY_COLORS[task.priority] || PRIORITY_COLORS.medium}`}>
            {task.priority}
          </span>
        </div>
        {task.due_date && (
          <span className="text-gray-400 flex items-center gap-1">
            <Calendar className="w-3 h-3" /> {task.due_date}
          </span>
        )}
      </div>
      
      {assignedUser && (
        <div className="mt-3 flex items-center gap-2">
          <div className="w-5 h-5 bg-[#84CC16] rounded-full flex items-center justify-center">
            <span className="text-white text-[9px]">{assignedUser.first_name?.[0]}{assignedUser.last_name?.[0]}</span>
          </div>
          <span className="text-xs text-gray-500">{assignedUser.first_name}</span>
        </div>
      )}
    </div>
  );
};

const TaskModal = ({ isOpen, onClose, task, onSave, users, projects }) => {
  const [formData, setFormData] = useState({
    title: '', description: '', status: 'todo', priority: 'medium',
    project_id: '', assigned_to: '', due_date: '',
  });

  useEffect(() => {
    if (task) {
      setFormData({
        title: task.title || '',
        description: task.description || '',
        status: task.status || 'todo',
        priority: task.priority || 'medium',
        project_id: task.project_id || '',
        assigned_to: task.assigned_to || '',
        due_date: task.due_date || '',
      });
    } else {
      setFormData({
        title: '', description: '', status: 'todo', priority: 'medium',
        project_id: '', assigned_to: '', due_date: '',
      });
    }
  }, [task]);

  if (!isOpen) return null;

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-bold text-[#0A1628]">
            {task ? 'Edit Task' : 'Add New Task'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          <div className="space-y-2">
            <Label>Task Title *</Label>
            <Input
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              required
              className="rounded-xl"
            />
          </div>

          <div className="space-y-2">
            <Label>Description</Label>
            <Textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={3}
              className="rounded-xl"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Status</Label>
              <Select value={formData.status} onValueChange={(v) => setFormData({ ...formData, status: v })}>
                <SelectTrigger className="rounded-xl">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TASK_COLUMNS.map(col => (
                    <SelectItem key={col.id} value={col.id}>{col.title}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Priority</Label>
              <Select value={formData.priority} onValueChange={(v) => setFormData({ ...formData, priority: v })}>
                <SelectTrigger className="rounded-xl">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Project</Label>
            <Select value={formData.project_id || "none"} onValueChange={(v) => setFormData({ ...formData, project_id: v === "none" ? "" : v })}>
              <SelectTrigger className="rounded-xl">
                <SelectValue placeholder="Select project" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No Project</SelectItem>
                {projects.map(project => (
                  <SelectItem key={project.id} value={project.id}>{project.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Assign To</Label>
              <Select value={formData.assigned_to || "none"} onValueChange={(v) => setFormData({ ...formData, assigned_to: v === "none" ? "" : v })}>
                <SelectTrigger className="rounded-xl">
                  <SelectValue placeholder="Select user" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Unassigned</SelectItem>
                  {users.map(user => (
                    <SelectItem key={user.id} value={user.id}>
                      {user.first_name} {user.last_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Due Date</Label>
              <Input
                type="date"
                value={formData.due_date}
                onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                className="rounded-xl"
              />
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button type="button" variant="outline" onClick={onClose} className="rounded-xl">
              Cancel
            </Button>
            <Button type="submit" className="bg-[#014DB7] hover:bg-[#003A8C] text-white rounded-xl">
              {task ? 'Update Task' : 'Create Task'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export const TasksPage = () => {
  const { api } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);

  const fetchData = async () => {
    try {
      const [tasksRes, usersRes, projectsRes] = await Promise.all([
        api.get('/api/tasks'),
        api.get('/api/users'),
        api.get('/api/projects'),
      ]);
      setTasks(tasksRes.data);
      setUsers(usersRes.data);
      setProjects(projectsRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load tasks');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleItemMove = async (itemId, newStatus) => {
    setTasks(prev => prev.map(task => 
      task.id === itemId ? { ...task, status: newStatus } : task
    ));

    try {
      await api.put(`/api/tasks/${itemId}`, { status: newStatus });
    } catch (error) {
      toast.error('Failed to update task');
      fetchData();
    }
  };

  const handleSaveTask = async (data) => {
    try {
      if (selectedTask) {
        await api.put(`/api/tasks/${selectedTask.id}`, data);
        toast.success('Task updated');
      } else {
        await api.post('/api/tasks', data);
        toast.success('Task created');
      }
      setModalOpen(false);
      setSelectedTask(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to save task');
    }
  };

  const handleDeleteTask = async (task) => {
    if (!window.confirm(`Delete task "${task.title}"?`)) return;
    
    try {
      await api.delete(`/api/tasks/${task.id}`);
      toast.success('Task deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete task');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-[#014DB7] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#0A1628]">Tasks</h1>
          <p className="text-gray-500">Manage your task board</p>
        </div>
        <Button onClick={() => { setSelectedTask(null); setModalOpen(true); }} className="bg-[#014DB7] hover:bg-[#003A8C] text-white rounded-xl">
          <Plus className="w-4 h-4 mr-2" /> Add Task
        </Button>
      </div>

      <KanbanBoard
        columns={TASK_COLUMNS}
        items={tasks}
        onItemMove={handleItemMove}
        renderItem={(task) => (
          <TaskCard 
            task={task} 
            users={users}
            projects={projects}
            onEdit={(t) => { setSelectedTask(t); setModalOpen(true); }}
            onDelete={handleDeleteTask}
          />
        )}
      />

      <TaskModal
        isOpen={modalOpen}
        onClose={() => { setModalOpen(false); setSelectedTask(null); }}
        task={selectedTask}
        onSave={handleSaveTask}
        users={users}
        projects={projects}
      />
    </div>
  );
};

export default TasksPage;
